/**
 * ScienceBackdrop — the same animated purple/indigo science-themed
 * backdrop used by the Fact-or-Myth Learn-More modal. Extracted into a
 * standalone component so it can be reused as a landing splash, a CTA
 * section, or any branded full-bleed surface.
 *
 * Honours prefers-reduced-motion: stops the bobbing/drifting animations
 * but keeps the gradient + icons visible.
 */
import { motion, useReducedMotion } from "framer-motion";

function FloatingIcon({ left, top, delay = 0, duration = 8, children, reduce }) {
  return (
    <motion.div
      className="absolute"
      style={{ left, top }}
      animate={reduce ? undefined : { y: [0, -18, 0], x: [0, 8, 0], rotate: [0, 6, -4, 0] }}
      transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
    >
      {children}
    </motion.div>
  );
}

function BeakerSvg({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" fill="none" className={className}>
      <path d="M24 6h16v6l8 28a10 10 0 0 1-9.6 13H25.6A10 10 0 0 1 16 40l8-28V6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="rgba(255,255,255,0.06)" />
      <path d="M18 32h28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="28" cy="44" r="2.2" fill="currentColor" />
      <circle cx="36" cy="48" r="1.6" fill="currentColor" />
      <circle cx="32" cy="52" r="1.2" fill="currentColor" />
    </svg>
  );
}
function PillSvg({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" fill="none" className={className}>
      <rect x="6" y="22" width="52" height="20" rx="10" stroke="currentColor" strokeWidth="2" fill="rgba(255,255,255,0.06)" />
      <path d="M32 22v20" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
function DnaSvg({ className = "" }) {
  return (
    <svg viewBox="0 0 64 80" fill="none" className={className}>
      <path d="M16 8 C 48 24, 16 40, 48 56 C 16 64, 48 72, 16 72" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M48 8 C 16 24, 48 40, 16 56 C 48 64, 16 72, 48 72" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {[18, 30, 42, 54, 66].map((y) => (
        <line key={y} x1="18" x2="46" y1={y} y2={y} stroke="currentColor" strokeWidth="1.2" opacity="0.55" />
      ))}
    </svg>
  );
}

export default function ScienceBackdrop({ className = "" }) {
  const reduce = useReducedMotion();
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden data-testid="science-backdrop">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/95 via-slate-900/95 to-indigo-900/95" />
      {/* Aurora blobs */}
      <motion.div
        className="absolute -left-40 -top-40 w-[28rem] h-[28rem] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(147,51,234,0.55), transparent 60%)" }}
        animate={reduce ? undefined : { x: [0, 40, -20, 0], y: [0, 20, -30, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -right-32 -bottom-32 w-[26rem] h-[26rem] rounded-full opacity-35"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.55), transparent 60%)" }}
        animate={reduce ? undefined : { x: [0, -30, 25, 0], y: [0, -20, 30, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute left-1/3 top-1/2 w-[22rem] h-[22rem] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, rgba(236,72,153,0.4), transparent 60%)" }}
        animate={reduce ? undefined : { x: [0, 20, -25, 0], y: [0, -25, 15, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Floating science icons */}
      <div className="absolute inset-0">
        <FloatingIcon left="6%" top="18%" duration={9} reduce={reduce}>
          <BeakerSvg className="w-16 h-16 text-purple-200/40" />
        </FloatingIcon>
        <FloatingIcon left="82%" top="14%" duration={11} delay={1} reduce={reduce}>
          <PillSvg className="w-20 h-20 text-pink-200/35" />
        </FloatingIcon>
        <FloatingIcon left="14%" top="68%" duration={10} delay={0.5} reduce={reduce}>
          <DnaSvg className="w-14 h-20 text-indigo-200/40" />
        </FloatingIcon>
        <FloatingIcon left="74%" top="62%" duration={12} delay={1.5} reduce={reduce}>
          <BeakerSvg className="w-12 h-12 text-purple-200/30" />
        </FloatingIcon>
        <FloatingIcon left="46%" top="8%" duration={10} delay={0.8} reduce={reduce}>
          <DnaSvg className="w-10 h-14 text-pink-200/30" />
        </FloatingIcon>
      </div>
    </div>
  );
}
